import React, { useEffect } from "react"
import {
  Box, Button, Center, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Spinner, Text,
} from "@chakra-ui/react"
import SimpleBar from "simplebar-react"
import Icon from "components/elements/Icon"
import { DIOBOX_UPDATES_LINK } from "utilities/externalLinks"
import { useUpdates } from "./queries"
import WhatsNewPost from "./WhatsNewPost"
import { WHATS_NEW_COLORS } from "./colors"

type Props = {
  left: number
  onSeen: (publishedAt: string) => void
  onClose: () => void
}

const WhatsNewPanel = ({ left, onSeen, onClose }: Props) => {
  const { data: posts = [], isLoading } = useUpdates()
  const newest = posts[0]?.publishedAt

  useEffect(() => {
    if (newest) {
      onSeen(newest)
    }
  }, [newest])

  let content = (
    <Box>
      {posts.map((post) => (
        <Box
          key={post.id}
          mx={-8}
          px={8}
          _notFirst={{
            mt: 14, pt: 14, borderTopWidth: "1px", borderColor: WHATS_NEW_COLORS.line,
          }}
        >
          <WhatsNewPost post={post} />
        </Box>
      ))}
    </Box>
  )
  if (isLoading) {
    content = <Center py={16}><Spinner color="gray.400" /></Center>
  } else if (!posts.length) {
    content = (
      <Text py={8} color={WHATS_NEW_COLORS.text2}>The latest updates couldn&apos;t be loaded.</Text>
    )
  }

  return (
    <Modal isOpen onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        position="fixed"
        left={`${left}px`}
        top={4}
        bottom={4}
        w={`calc(100vw - ${left + 16}px)`}
        maxW="30rem"
        maxH="none"
        my={0}
        borderRadius="xl"
        boxShadow="0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)"
        overflow="hidden"
        motionProps={{
          initial: { opacity: 0, x: -8 },
          animate: { opacity: 1, x: 0, transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] } },
          exit: { opacity: 0, x: -8, transition: { duration: 0.15, ease: "easeIn" } },
        }}
      >
        <ModalHeader
          px={8}
          pt={8}
          pb={2}
          fontSize="32px"
          fontWeight="semibold"
          letterSpacing="-0.02em"
          color={WHATS_NEW_COLORS.text1}
        >
          What&apos;s New
        </ModalHeader>
        <ModalCloseButton top={6} right={6} color="gray.500" />
        <Box as={SimpleBar} flex={1} minH={0}>
          <ModalBody px={8} pt={8} pb={10}>
            {content}
          </ModalBody>
        </Box>
        <ModalFooter px={6} pt={3} pb={6}>
          <Button
            as="a"
            href={DIOBOX_UPDATES_LINK}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline"
            w="full"
            h={12}
            borderRadius="full"
            borderColor={WHATS_NEW_COLORS.line}
            color={WHATS_NEW_COLORS.text1}
            fontSize="17px"
            fontWeight="normal"
          >
            View all updates
            <Icon icon="link-external" size={3} ml={2} />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default WhatsNewPanel
